import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { getProductName } from 'app/utils/product-utils';
import {
  AppProductLine as ProductLine,
  ProductAssignment, ProductPositionsAssignmentsQueryVars, SnkProduct
} from 'graphql/models/extended';
import {
  ProductPositionsAssignment,
  ProductPositionsAssignmentsOutput
} from 'graphql/models/generated';
import { OfferPositionProductService } from 'shared/services/offer-position-product.service';
import { ProductPositionsAssignmentsQuery } from 'graphql/queries/product-positions-assignments';

@Injectable({
  providedIn: 'root'
})
export class PositionProductAssignmentsService {
  constructor(
    private offerPositionProductService: OfferPositionProductService,
    private productPositionsAssignmentsQuery: ProductPositionsAssignmentsQuery
  ) { }

  getProductPositionsAssignments(
    variables: ProductPositionsAssignmentsQueryVars
  ): Observable<ProductPositionsAssignmentsOutput[]> {

    return this.productPositionsAssignmentsQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe(
      map(
        (r) => r.data?.productPositionsAssignments as ProductPositionsAssignmentsOutput[]
      ),
      take(1)
    );
  }

  mapPositionProductAssignments(pos: ProductLine, assignmentsData?: ProductPositionsAssignmentsOutput): SnkProduct {
    let productAssignments: ProductAssignment[] = pos?.product.productAssignments || [];
    let positionAssignments: ProductAssignment[] = pos?.product.positionAssignments || [];
    let assignments: ProductAssignment[] = [];

    const product = { ...pos.product } as SnkProduct;
    const baseProduct = this.offerPositionProductService.getProductBaseFromLine(pos) as SnkProduct;

    product.productName = getProductName(pos);

    if (assignmentsData?.assignmentsValues) {
      assignments = assignmentsData.assignmentsValues.map((assignment) =>
        this.extractPositionProductAssignment(assignment)
      );
    }

    if (assignmentsData?.productAssignmentsValues) {
      productAssignments = assignmentsData.productAssignmentsValues.map((assignment) =>
        this.extractPositionProductAssignment(assignment)
      );
    }

    if (assignmentsData?.positionAssignmentsValues?.length) {
      positionAssignments = assignmentsData.positionAssignmentsValues.map((assignment) =>
        this.extractPositionProductAssignment(assignment)
      );
    } else {
      positionAssignments = [];
    }

    return {
      ...product,
      ...baseProduct,
      assignments,
      productAssignments,
      positionAssignments
    };
  }

  extractPositionProductAssignment(assignment: ProductPositionsAssignment): ProductAssignment {
    return {
      id: assignment.id,
      varName: assignment.varName,
      name: assignment.name,
      value: assignment.value
    } as ProductAssignment;
  }

  mapPositionProductAssignmentsDifferences(pos: ProductLine, parentPosition: ProductLine): SnkProduct {
    const position = { ...pos };
    const product = { ...pos.product } as SnkProduct;
    const currentAssignments = position?.product?.assignments as ProductAssignment[];
    const parentAssignments = parentPosition?.product?.assignments as ProductAssignment[];

    // extract position assignments/differences,
    // by comparing them to the parent
    if (currentAssignments && parentAssignments) {
      product.positionAssignments = this.filterAssignments(currentAssignments, parentAssignments).filter((item) => this.removeDuplicateAssignments(item));
    }

    return product;
  }

  filterAssignments(
    posList: ProductAssignment[],
    baseList: ProductAssignment[],
    compareValue = true
  ): ProductAssignment[] {
    return posList.filter((a) => !baseList.some((b) => b.id === a.id && (compareValue ? b.value === a.value : true)));
  }

  removeDuplicateAssignments(item: ProductAssignment): boolean {
    // this regex filtering dimensions and measurements fields for all manufactures
    const regexData = [
      '(.(?:Z_|STZ_|FU_|BB_|TB_|):?(Breite|Hoehe|DIN|Dicke|Wandstaerke|ATTRIB|Masse?))'
    ];
    // regex demo: https://regex101.com/r/nvNabG/1

    const regex = new RegExp(`${regexData}`, 'ig');
    const exceptions = ['Leibungsverkleidung', 'Glas.'];

    return !regex.test(item.id) || exceptions.some((e) => item.id.includes(e));
  }
}
